import { segment } from '@hotjar/tracking';
import type { FunnelResult, FunnelQuery } from '@hotjar/dashboard-quantz';
import { round } from 'lodash-es';

import { ChartType } from '@hotjar/dashboard-quantz';
import { getDateRangeLabel, getAxisResolution } from '../AsyncDashboardQuantzPage/trackMethods';

interface SegmentData {
  /* eslint-disable camelcase */
  account_id: number;
  site_id: number;
  organization_id: number;
  permission_level: string;
  shown_traffic_coverage: number;
  state_event_was_triggered_on: string;
  /* eslint-enable camelcase */
}

export const trackFunnelViewed = (
  segmentData: SegmentData,
  currentFunnelQuery: FunnelQuery,
  funnelsResultList?: FunnelResult[],
): void => {
  const { dateRange, filterChainSeries, steps, chartType } = currentFunnelQuery;

  let funnelChartType: string;
  let funnelXAxisResolutionSelection = '';
  let funnelXAxisResolutionShown = '';
  if (chartType === ChartType.bar) {
    funnelChartType = 'steps';
    funnelXAxisResolutionSelection = 'auto';
  } else if (chartType === ChartType.number) {
    funnelChartType = 'metric';
  } else if (chartType === ChartType.timeline) {
    funnelChartType = 'conversion_over_time';
    funnelXAxisResolutionSelection = 'auto';
    funnelXAxisResolutionShown = getAxisResolution(currentFunnelQuery.dateRange);
  } else {
    // It shouldn't happen
    return;
  }

  let data: segment.SegmentTrackData = {
    ...segmentData,
    funnel_id: '',
    funnel_title: '',
    funnel_description: '',
    funnel_save_state: 'unsaved',
    funnel_added_to_dashboard: false,
    date_range: getDateRangeLabel(dateRange),
    number_steps_added: steps.length,
    steps_added: steps.map((step) => step.stepInUiFormat[0].kind).flat(),
    steps_added_expanded: steps.map((step) => step.label),
    funnel_chart_type: funnelChartType,
    funnel_x_axis_resolution_selection: funnelXAxisResolutionSelection,
    funnel_x_axis_resolution_shown: funnelXAxisResolutionShown,
    number_filter_groups_applied: filterChainSeries.length,
    filters_included_excluding_steps: filterChainSeries
      .map((item) => item.filtersInUiFormat)
      .flat()
      .map((filter) => filter.kind)
      .flat()
      .sort(),
  };

  if (funnelsResultList) {
    /* eslint-disable camelcase */
    const number_matching_sessions_starting_funnels: number[] = [];
    const overall_conversion_rates_percentage_all_steps: number[] = [];
    const overall_dropoff_rates_percentage_all_steps: number[] = [];
    const overall_average_times_to_convert_in_seconds_all_steps: number[] = [];

    funnelsResultList.forEach((funnelsResult) => {
      const { globalResult, stepResultList } = funnelsResult.result;
      const startingSessionsCount = stepResultList[0].result.convertedSessionCount;

      number_matching_sessions_starting_funnels.push(startingSessionsCount);
      // convert to one digit after coma
      overall_conversion_rates_percentage_all_steps.push(
        startingSessionsCount === 0
          ? 0
          : round((globalResult.convertedSessionCount / startingSessionsCount) * 100, 1),
      );
      overall_dropoff_rates_percentage_all_steps.push(
        startingSessionsCount === 0
          ? 0
          : round((globalResult.notConvertedSessionCount / startingSessionsCount) * 100, 1),
      );
      // convert ms to seconds
      overall_average_times_to_convert_in_seconds_all_steps.push(
        round(globalResult.cumulativeTimeAverage / 1000),
      );
    });

    data = {
      ...data,
      number_matching_sessions_starting_funnels,
      overall_conversion_rates_percentage_all_steps,
      overall_dropoff_rates_percentage_all_steps,
      overall_average_times_to_convert_in_seconds_all_steps,
    };
    /* eslint-enable camelcase */
  }

  segment.track('Funnel Viewed', data);
};
